import { forwardRef } from 'react';
import { FileSignature } from 'lucide-react';
import type { SignedConsent, ConsentTemplate, StoreInfo } from '../types';

interface ConsentPreviewProps {
    consent: SignedConsent | null;
    templates: ConsentTemplate[];
    storeInfo: StoreInfo;
}

export const ConsentPreview = forwardRef<HTMLDivElement, ConsentPreviewProps>(
    ({ consent, templates, storeInfo }, ref) => {
        if (!consent) {
            return (
                <div className="flex items-center justify-center h-full text-neutral-400 dark:text-neutral-500 dark:text-neutral-400 dark:text-neutral-500">
                    <div className="text-center">
                        <FileSignature className="h-12 w-12 mx-auto mb-2" />
                        <p>동의서를 선택해주세요</p>
                    </div>
                </div>
            );
        }

        const template = templates.find((t) => String(t.id) === consent.templateId);

        return (
            <div ref={ref} className="bg-white dark:bg-neutral-900 rounded-lg shadow p-6">
                {/* 동의서 제목 */}
                <h3 className="text-xl font-bold text-center mb-6 pb-4 border-b-2">
                    {consent.templateName}
                </h3>

                {/* 매장정보 + 보호자/반려동물 정보 */}
                <div className="mb-6 grid grid-cols-2 gap-4">
                    {/* 매장정보 */}
                    <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                        <h4 className="text-sm font-semibold mb-3 text-neutral-900 dark:text-neutral-100">매장정보</h4>
                        <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                            <div className="flex">
                                <span className="font-medium w-20 shrink-0">매장명:</span>
                                <span>{storeInfo.name}</span>
                            </div>
                            <div className="flex">
                                <span className="font-medium w-20 shrink-0">주소:</span>
                                <span className="break-all">{storeInfo.address}</span>
                            </div>
                            <div className="flex">
                                <span className="font-medium w-20 shrink-0">전화번호:</span>
                                <span>{storeInfo.phone}</span>
                            </div>
                        </div>
                    </div>

                    {/* 보호자 및 반려동물 정보 */}
                    <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                        <h4 className="text-sm font-semibold mb-3 text-neutral-900 dark:text-neutral-100">보호자 및 반려동물</h4>
                        <div className="text-sm text-neutral-600 dark:text-neutral-300 space-y-1">
                            <div className="flex">
                                <span className="font-medium w-20 shrink-0">보호자:</span>
                                <span>{consent.customerName}</span>
                            </div>
                            <div className="flex">
                                <span className="font-medium w-20 shrink-0">연락처:</span>
                                <span>{consent.customerPhone}</span>
                            </div>
                            <div className="flex">
                                <span className="font-medium w-20 shrink-0">반려동물:</span>
                                <span>{consent.petName}</span>
                            </div>
                            <div className="flex">
                                <span className="font-medium w-20 shrink-0">종/품종:</span>
                                <span>
                                    {consent.petSpecies}
                                    {consent.petBreed ? ` - ${consent.petBreed}` : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 동의서 본문 */}
                <div className="mb-6">
                    <div
                        className="prose prose-sm max-w-none
                            [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h1]:mt-6
                            [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mb-3 [&_h2]:mt-5
                            [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-4
                            [&_p]:mb-3 [&_p]:leading-relaxed
                            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
                            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
                            [&_li]:mb-1
                            [&_strong]:font-bold
                            [&_em]:italic
                            [&_u]:underline
                            [&_s]:line-through
                            text-sm text-neutral-700 dark:text-neutral-200"
                        dangerouslySetInnerHTML={{
                            __html: template?.content || ''
                        }}
                    />
                </div>

                {/* 서명란 */}
                <div className="pt-6 border-t-2 mt-6">
                    <div className="text-right">
                        <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                            위 내용을 확인하였으며, 동의합니다.
                        </p>
                        <p className="text-sm text-neutral-600 dark:text-neutral-300">
                            {new Date(new Date(consent.signedAt).getTime() + 9 * 60 * 60 * 1000).toLocaleDateString('ko-KR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                timeZone: 'UTC'
                            })}
                        </p>
                        <p className="text-lg font-semibold mt-2">
                            {consent.customerName} (인)
                        </p>
                        {consent.signatureImageUrl && (
                            <div className="mt-3 inline-block p-2 border rounded bg-white dark:bg-neutral-900 signature-container">
                                <img
                                    src={consent.signatureImageUrl}
                                    alt="서명"
                                    className="max-h-16 block signature-image"
                                    style={{
                                        maxHeight: '64px',
                                        display: 'block',
                                        width: 'auto',
                                        height: 'auto'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

ConsentPreview.displayName = 'ConsentPreview';
